import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download, Eye, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { VerificationCallButton } from "./VerificationCall";

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const kb = (n) => `${Math.round((n || 0) / 1024)} KB`;

const STATUS_TONE = {
  incomplete_onboarding: "text-[#8A8A82] border-white/20",
  pending_kyc: "text-[#D8B23E] border-[#D8B23E]/40",
  awaiting_approval: "text-[#B87333] border-[#B87333]/40",
  active: "text-[#2FB999] border-[#2FB999]/40",
};

export function AdminOnboardingInbox({ api }) {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUid, setOpenUid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/onboarding-inbox"); setRows(r.data || []); }
    catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="text-[#F5F5F0]">
      <div className="mb-8">
        <div className="overline text-[#B87333] mb-1">Onboarding Inbox</div>
        <h2 className="font-serif text-3xl">Registration Files & Documents</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-2">Every EOI, incomplete registration, and KYC file lands here. Expand a row to review uploaded documents.</p>
      </div>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div> :
        rows.length === 0 ? <div className="p-16 text-center text-[#8A8A82] font-body text-sm border border-white/10 rounded-2xl">No registrations awaiting review.</div> :
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-white/10 rounded-sm bg-white/[0.02]">
                <button data-testid={`ob-inbox-row-${r.id}`} onClick={() => setOpenUid(openUid === r.id ? null : r.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02]">
                  <div className="flex items-center gap-4 text-left">
                    <span className="text-[10px] tracking-[0.2em] tabular-nums px-2.5 py-1 rounded-sm border border-[#B87333]/55 text-[#B87333] bg-[#B87333]/[0.07] font-semibold font-grotesk">
                      #{r.vendor_code || "—"}
                    </span>
                    <div>
                      <div className="font-serif text-lg">{r.business_name || r.email}</div>
                      <div className="text-[11px] text-[#8A8A82]">{r.email} · {r.role || "—"} · {r.documents_count} doc(s) · created {fmt(r.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] font-grotesk">{r.completion_pct ?? 20}%</span>
                    <span className={`inline-block rounded-full border px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] font-grotesk ${STATUS_TONE[r.status] || "text-[#8A8A82] border-white/20"}`}>
                      {(r.status || "—").replace(/_/g, " ")}
                    </span>
                    <span onClick={(e) => e.stopPropagation()}>
                      <VerificationCallButton api={api} currentUser={currentUser}
                        target={{ id: r.id, email: r.email, business_name: r.business_name }}
                        variant="ghost" label="Schedule Meet" />
                    </span>
                    {openUid === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>
                {openUid === r.id && <UserDocsPanel api={api} uid={r.id} />}
              </div>
            ))}
          </div>
      }
    </div>
  );
}

function UserDocsPanel({ api, uid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/admin/onboarding-docs/${uid}`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => toast.error("Could not load user file"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [api, uid]);

  const downloadDoc = async (docId, filename) => {
    try {
      const r = await api.get(`/admin/onboarding-docs/${uid}/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "document"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const inlineDoc = async (docId) => {
    try {
      const r = await api.get(`/admin/onboarding-docs/${uid}/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch { toast.error("Preview failed"); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#B87333]" /></div>;
  if (!data) return null;
  const p = data.profile || {};
  const docs = data.documents || [];

  return (
    <div className="border-t border-white/10 p-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-[#C9C9C2]">
        <Info label="Legal Name" value={p.business_name || "—"} />
        <Info label="PAN" value={p.pan_number || "—"} />
        <Info label="GSTIN" value={p.gst_number || "—"} />
        <Info label="State" value={p.state || "—"} />
        <Info label="Business Type" value={p.business_type || "—"} />
        <Info label="Years of Ops" value={p.years_of_operation || "—"} />
        <Info label="Turnover (INR)" value={p.annual_turnover_inr ? Number(p.annual_turnover_inr).toLocaleString("en-IN") : "—"} />
        <Info label="Material Focus" value={(p.material_focus || []).join(", ") || "—"} />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#B87333] font-grotesk mb-3">KYC Documents Vault</div>
        {docs.length === 0 ? <div className="text-xs text-[#8A8A82] italic">No documents uploaded yet.</div> :
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`admin-doc-${d.id}`} className="flex items-center justify-between border border-white/10 rounded-sm p-3">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-sm bg-[#B87333]/10 text-[#B87333]"><FileText className="w-4 h-4" /></span>
                  <div className="text-xs">
                    <div className="text-[#F5F5F0]">{d.filename} <span className="text-[10px] text-[#8A8A82] ml-2 uppercase tracking-[0.15em]">{d.slot.replace(/_/g, " ")}</span></div>
                    <div className="text-[10px] text-[#8A8A82]">{d.content_type} · {kb(d.size_bytes)} · {fmt(d.uploaded_at)}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`admin-doc-view-${d.id}`} onClick={() => inlineDoc(d.id)}
                    className="rounded-full border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:border-[#B87333] flex items-center gap-1">
                    <Eye className="w-3 h-3" /> View
                  </button>
                  <button data-testid={`admin-doc-dl-${d.id}`} onClick={() => downloadDoc(d.id, d.filename)}
                    className="rounded-full bg-[#B87333] text-black px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-grotesk hover:brightness-110 flex items-center gap-1">
                    <Download className="w-3 h-3" /> Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-0.5">{label}</div><div>{value}</div></div>;
}
